import type {
  EditableRegion,
  Point,
  PolygonRegion,
  ShapeKind,
  ShapeRegion,
  StrokeRegion,
} from "./types";

/** The subset of the 2D context API shared by canvas, OffscreenCanvas and Konva. */
export interface PathContext {
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  closePath(): void;
  ellipse(
    x: number,
    y: number,
    radiusX: number,
    radiusY: number,
    rotation: number,
    startAngle: number,
    endAngle: number,
  ): void;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Traces a filled outline in the region's local coordinates. */
export function traceShape(
  context: PathContext,
  kind: ShapeKind,
  width: number,
  height: number,
): void {
  context.beginPath();
  if (kind === "ellipse") {
    context.ellipse(width / 2, height / 2, width / 2, height / 2, 0, 0, Math.PI * 2);
  } else if (kind === "triangle") {
    context.moveTo(width / 2, 0);
    context.lineTo(width, height);
    context.lineTo(0, height);
    context.closePath();
  } else {
    context.moveTo(0, 0);
    context.lineTo(width, 0);
    context.lineTo(width, height);
    context.lineTo(0, height);
    context.closePath();
  }
}

export function tracePolyline(
  context: PathContext,
  points: Point[],
  closed: boolean,
): void {
  context.beginPath();
  if (points.length === 0) return;
  context.moveTo(points[0].x, points[0].y);
  // A single tap still needs a segment for round caps to paint a dot.
  if (points.length === 1) context.lineTo(points[0].x + 0.01, points[0].y);
  for (let index = 1; index < points.length; index += 1) {
    context.lineTo(points[index].x, points[index].y);
  }
  if (closed) context.closePath();
}

export function flatPoints(points: Point[]): number[] {
  return points.flatMap((point) => [point.x, point.y]);
}

export function normalizeRect(start: Point, end: Point, square = false): Rect {
  let width = end.x - start.x;
  let height = end.y - start.y;
  if (square) {
    const side = Math.max(Math.abs(width), Math.abs(height));
    width = Math.sign(width || 1) * side;
    height = Math.sign(height || 1) * side;
  }
  return {
    x: Math.min(start.x, start.x + width),
    y: Math.min(start.y, start.y + height),
    width: Math.abs(width),
    height: Math.abs(height),
  };
}

/** Drops points closer than `tolerance` to the previously kept one. */
export function simplifyPoints(points: Point[], tolerance: number): Point[] {
  if (points.length <= 2) return points;
  const kept = [points[0]];
  for (let index = 1; index < points.length - 1; index += 1) {
    const last = kept[kept.length - 1];
    if (Math.hypot(points[index].x - last.x, points[index].y - last.y) >= tolerance) {
      kept.push(points[index]);
    }
  }
  kept.push(points[points.length - 1]);
  return kept;
}

/** Splits absolute points into an origin and points relative to it. */
export function toRelativePoints(points: Point[]): {
  origin: Point;
  points: Point[];
} {
  const origin = points[0];
  return {
    origin,
    points: points.map((point) => ({ x: point.x - origin.x, y: point.y - origin.y })),
  };
}

function localCorners(region: EditableRegion): Point[] {
  if (region.kind === "stroke" || region.kind === "polygon") {
    const xs = region.points.map((point) => point.x);
    const ys = region.points.map((point) => point.y);
    const pad = region.kind === "stroke" ? region.penWidth / 2 : 0;
    const left = Math.min(...xs) - pad;
    const right = Math.max(...xs) + pad;
    const top = Math.min(...ys) - pad;
    const bottom = Math.max(...ys) + pad;
    return [
      { x: left, y: top },
      { x: right, y: top },
      { x: right, y: bottom },
      { x: left, y: bottom },
    ];
  }
  return [
    { x: 0, y: 0 },
    { x: region.width, y: 0 },
    { x: region.width, y: region.height },
    { x: 0, y: region.height },
  ];
}

/** Axis-aligned bounds in image coordinates, including rotation and brush. */
export function regionBounds(region: EditableRegion): Rect {
  const angle = (region.rotation * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const corners = localCorners(region).map((point) => ({
    x: region.x + point.x * cos - point.y * sin,
    y: region.y + point.x * sin + point.y * cos,
  }));
  const xs = corners.map((point) => point.x);
  const ys = corners.map((point) => point.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

/** Bounds of an absolute-coordinate brush stroke. */
export function strokeBounds(points: Point[], penWidth: number): Rect {
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const pad = penWidth / 2;
  const x = Math.min(...xs) - pad;
  const y = Math.min(...ys) - pad;
  return {
    x,
    y,
    width: Math.max(...xs) + pad - x,
    height: Math.max(...ys) + pad - y,
  };
}

export function intersects(a: Rect, b: Rect): boolean {
  return (
    a.x < b.x + b.width &&
    b.x < a.x + a.width &&
    a.y < b.y + b.height &&
    b.y < a.y + a.height
  );
}

/**
 * Folds a transformer scale into the region's geometry. Brush width follows
 * the area change, so uniform scaling keeps the stroke's proportions and a
 * one-axis stretch does not flatten the brush.
 */
export function bakeScale<T extends EditableRegion>(
  region: T,
  scaleX: number,
  scaleY: number,
): T {
  if (region.kind === "stroke" || region.kind === "polygon") {
    const scaled = region.points.map((point) => ({
      x: point.x * scaleX,
      y: point.y * scaleY,
    }));
    if (region.kind === "polygon") {
      return { ...(region as PolygonRegion), points: scaled } as T;
    }
    return {
      ...(region as StrokeRegion),
      points: scaled,
      penWidth: Math.max(1, region.penWidth * Math.sqrt(Math.abs(scaleX * scaleY))),
    } as T;
  }
  return {
    ...(region as ShapeRegion),
    width: region.width * scaleX,
    height: region.height * scaleY,
  } as T;
}
